// HV Busbar Sizing Calculator: field metadata, defaults, option lists and client-side
// validation for instant inline feedback. All the physics (heat balance, AC factor,
// transient, short circuit, recommend search) and the authoritative validation live in
// the backend -- see src/lib/toolsApi.js (busbarToolApi) and
// backend/tools/busbar_sizing_tool.py. Every rule in validateInputs() mirrors that file so
// the UI never sends a request that would get an HTTP 422. Keep min/max and defaults in
// sync with backend/core/lookup/busbar_specs.py (tests/test_busbar_tool.py checks it).

export const MATERIALS = [
  { value: 'cu_etp', label: 'Copper Cu-ETP (C11000)', iacs: 100, alpha20: 0.00393, alphaRefIacs: 100, base: 'cu', scSourced: true },
  { value: 'al_1350', label: 'Aluminium 1350 (EC grade)', iacs: 61, alpha20: 0.00403, alphaRefIacs: 61, base: 'al', scSourced: false },
  { value: 'al_6101', label: 'Aluminium 6101-T61', iacs: 57, alpha20: 0.00403, alphaRefIacs: 61, base: 'al', scSourced: false, approximation: true },
];
export const MATERIAL_BY_KEY = Object.fromEntries(MATERIALS.map((m) => [m.value, m]));

export const FINISHES = [
  { value: 'bright', label: 'Bare, bright metal', emissivity: 0.10 },
  { value: 'partial', label: 'Bare, partially oxidised', emissivity: 0.30 },
  { value: 'heavy', label: 'Bare, heavily oxidised', emissivity: 0.70 },
  { value: 'tin', label: 'Tin-plated', emissivity: 0.04 },
  { value: 'painted', label: 'Painted (dull)', emissivity: 0.90 },
  { value: 'sleeve', label: 'Insulated sleeve', emissivity: 0.90 },
  { value: 'custom', label: 'Custom emissivity', emissivity: null },
];

export const TEMP_LIMITS = [
  { value: 'din65', label: '65 °C — DIN 43671 basis', tempC: 65 },
  { value: 'iec105', label: '105 °C — IEC 61439 terminals', tempC: 105 },
  { value: 'iec140', label: '140 °C — IEC 61439 bare Cu max', tempC: 140 },
  { value: 'custom', label: 'Custom', tempC: null },
];

export const PROFILES = [
  { value: 'none', label: 'Continuous only' },
  { value: 'peak', label: 'Single peak' },
  { value: 'duty', label: 'Repeating duty cycle' },
];

export const DEFAULT_INPUTS = {
  mode: 'check',
  material: 'cu_etp',
  conductivityIacs: 100,
  widthMm: 30,
  thicknessMm: 5,
  edgeRadiusMm: 0,
  lengthM: 0.5,
  nBars: 1,
  spacingMm: 5,
  currentA: 300,
  currentType: 'dc',
  frequencyHz: 50,
  profile: 'none',
  peakCurrentA: 500,
  peakDurationS: 10,
  periodS: 60,
  ambientC: 40,
  tempLimit: 'iec105',
  maxTempC: 105,
  orientation: 'edge',
  finish: 'partial',
  emissivity: 0.5,
  sleeveThicknessMm: 0.5,
  sleeveConductivity: 0.2,
  environment: 'free',
  wallEmissivity: 0.9,
  airVelocity: 0,
  faultCurrentKa: 10,
  clearingTimeMs: 20,
  scMaxTempC: 200,
  systemVoltageV: 400,
  maxDropPercent: null,
};

// "Example" scenario (also the guide's worked example): a tin-plated 40x5 Cu link inside an
// 800 V pack junction box, with a 60 s peak and a 20 kA fault.
export const EXAMPLE_INPUTS = {
  ...DEFAULT_INPUTS,
  widthMm: 40,
  thicknessMm: 5,
  lengthM: 0.6,
  finish: 'tin',
  environment: 'enclosed',
  wallEmissivity: 0.9,
  ambientC: 55,
  currentA: 350,
  profile: 'peak',
  peakCurrentA: 900,
  peakDurationS: 60,
  tempLimit: 'iec105',
  faultCurrentKa: 20,
  clearingTimeMs: 30,
  systemVoltageV: 800,
  maxDropPercent: 0.5,
};

export const CATEGORICAL_KEYS = ['mode', 'material', 'currentType', 'profile', 'tempLimit', 'orientation', 'finish', 'environment'];
export const CATEGORICAL_OPTIONS = {
  mode: ['check', 'recommend'],
  material: MATERIALS.map((m) => m.value),
  currentType: ['dc', 'ac'],
  profile: PROFILES.map((p) => p.value),
  tempLimit: TEMP_LIMITS.map((t) => t.value),
  orientation: ['edge', 'flat'],
  finish: FINISHES.map((f) => f.value),
  environment: ['free', 'enclosed'],
};

export const NUMERIC_FIELD_SPECS = [
  { key: 'conductivityIacs', label: 'Conductivity', unit: '% IACS', min: 20, max: 105, strictlyPositive: true },
  { key: 'widthMm', label: 'Width', unit: 'mm', min: 1, max: 300, strictlyPositive: true },
  { key: 'thicknessMm', label: 'Thickness', unit: 'mm', min: 0.5, max: 50, strictlyPositive: true },
  { key: 'edgeRadiusMm', label: 'Edge Radius', unit: 'mm', min: 0, max: 25, strictlyPositive: false },
  { key: 'lengthM', label: 'Length', unit: 'm', min: 0.01, max: 100, strictlyPositive: true },
  { key: 'nBars', label: 'Bars per Pole', unit: '', min: 1, max: 4, strictlyPositive: true, integer: true },
  { key: 'spacingMm', label: 'Gap Between Bars', unit: 'mm', min: 0.5, max: 200, strictlyPositive: true },
  { key: 'currentA', label: 'Continuous Current', unit: 'A', min: 1, max: 20000, strictlyPositive: true },
  { key: 'frequencyHz', label: 'Frequency', unit: 'Hz', min: 1, max: 2000, strictlyPositive: true },
  { key: 'peakCurrentA', label: 'Peak Current', unit: 'A', min: 1, max: 50000, strictlyPositive: true },
  { key: 'peakDurationS', label: 'Peak Duration', unit: 's', min: 0.1, max: 3600, strictlyPositive: true },
  { key: 'periodS', label: 'Cycle Period', unit: 's', min: 0.2, max: 7200, strictlyPositive: true },
  { key: 'ambientC', label: 'Ambient Air', unit: '°C', min: -40, max: 125, strictlyPositive: false },
  { key: 'maxTempC', label: 'Max Conductor Temp', unit: '°C', min: 30, max: 250, strictlyPositive: true },
  { key: 'emissivity', label: 'Surface Emissivity', unit: '', min: 0.02, max: 1, strictlyPositive: true },
  { key: 'sleeveThicknessMm', label: 'Sleeve Wall', unit: 'mm', min: 0.1, max: 5, strictlyPositive: true },
  { key: 'sleeveConductivity', label: 'Sleeve Conductivity', unit: 'W/m·K', min: 0.05, max: 5, strictlyPositive: true },
  { key: 'wallEmissivity', label: 'Wall Emissivity', unit: '', min: 0.05, max: 1, strictlyPositive: true },
  { key: 'airVelocity', label: 'Air Velocity', unit: 'm/s', min: 0, max: 20, strictlyPositive: false },
  { key: 'faultCurrentKa', label: 'Prospective Fault Current', unit: 'kA', min: 0.01, max: 500, strictlyPositive: true },
  { key: 'clearingTimeMs', label: 'Clearing Time', unit: 'ms', min: 0.1, max: 5000, strictlyPositive: true },
  { key: 'scMaxTempC', label: 'Max Short-Circuit Temp', unit: '°C', min: 100, max: 400, strictlyPositive: true },
  { key: 'systemVoltageV', label: 'System Voltage', unit: 'V', min: 1, max: 1500, strictlyPositive: true, optional: true },
  { key: 'maxDropPercent', label: 'Max Voltage Drop', unit: '%', min: 0.01, max: 20, strictlyPositive: true, optional: true },
];
export const SPEC_BY_KEY = Object.fromEntries(NUMERIC_FIELD_SPECS.map((s) => [s.key, s]));

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return spec.optional ? null : 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value) || !Number.isFinite(value)) return 'Must be a number.';
  if (spec.strictlyPositive && value <= 0) return 'Must be greater than 0.';
  if (spec.min !== null && value < spec.min) return `Must be ≥ ${spec.min}.`;
  if (spec.max !== null && value > spec.max) return `Must be ≤ ${spec.max}.`;
  if (spec.integer && !Number.isInteger(value)) return 'Must be a whole number.';
  return null;
}

// Query-string prefill (?material=al_1350&widthMm=60&currentA=800 ...): numeric fields use
// validateField, the categorical ones must be one of their options.
export const PREFILL_SPECS = [
  ...NUMERIC_FIELD_SPECS,
  ...CATEGORICAL_KEYS.map((key) => ({ key, label: key, unit: '', options: CATEGORICAL_OPTIONS[key] })),
];
export function validatePrefill(spec, raw) {
  if (spec.options) return spec.options.includes(raw) ? null : `Must be one of: ${spec.options.join(', ')}.`;
  return validateField(spec, raw);
}

export const tempLimitC = (v) => {
  const preset = TEMP_LIMITS.find((t) => t.value === v.tempLimit);
  return preset && preset.tempC !== null ? preset.tempC : Number(v.maxTempC);
};

// Numeric fields the current selections need (mirrors required_fields() in the backend).
export function requiredKeys(v) {
  const req = ['conductivityIacs', 'lengthM', 'nBars', 'currentA', 'ambientC', 'airVelocity',
    'faultCurrentKa', 'clearingTimeMs', 'scMaxTempC'];
  if (v.mode === 'check') req.push('widthMm', 'thicknessMm', 'edgeRadiusMm');
  if (v.currentType === 'ac') req.push('frequencyHz');
  if (v.profile === 'peak' || v.profile === 'duty') req.push('peakCurrentA', 'peakDurationS');
  if (v.profile === 'duty') req.push('periodS');
  if (v.tempLimit === 'custom') req.push('maxTempC');
  if (v.finish === 'custom') req.push('emissivity');
  if (v.finish === 'sleeve') req.push('sleeveThicknessMm', 'sleeveConductivity');
  if (v.environment === 'enclosed') req.push('wallEmissivity');
  return req;
}

// Returns { errors, payload }: every server rule, applied client-side.
export function validateInputs(raw) {
  const errors = {};
  const values = {};
  CATEGORICAL_KEYS.forEach((k) => { values[k] = raw[k]; });
  const req = requiredKeys(values);
  const check = (key) => {
    const err = validateField(SPEC_BY_KEY[key], raw[key]);
    if (err) errors[key] = err;
    else values[key] = raw[key] === '' || raw[key] === null ? null : Number(raw[key]);
  };
  req.forEach((key) => check(key));
  if (!errors.nBars && values.nBars > 1) check('spacingMm');
  ['systemVoltageV', 'maxDropPercent'].forEach((key) => check(key));

  if (values.mode === 'check' && !errors.widthMm && !errors.thicknessMm && !errors.edgeRadiusMm) {
    if (values.thicknessMm > values.widthMm) {
      errors.thicknessMm = 'Thickness must not exceed the width (enter the larger side as the width).';
    } else if (values.edgeRadiusMm > values.thicknessMm / 2) {
      errors.edgeRadiusMm = `Edge radius must be at most half the thickness (${values.thicknessMm / 2} mm).`;
    }
  }
  if (values.profile === 'duty' && !errors.peakDurationS && !errors.periodS && values.peakDurationS >= values.periodS) {
    errors.periodS = 'The cycle period must be longer than the peak duration.';
  }
  if (values.profile !== 'none' && !errors.peakCurrentA && !errors.currentA && values.peakCurrentA <= values.currentA) {
    errors.peakCurrentA = `The peak current must be above the continuous current (${values.currentA} A).`;
  }
  if (values.maxDropPercent !== null && values.maxDropPercent !== undefined && values.systemVoltageV === null && !errors.systemVoltageV) {
    errors.systemVoltageV = 'Enter the system voltage to use a voltage-drop limit.';
  }
  if (!errors.ambientC && !errors.maxTempC) {
    const tLim = tempLimitC(values);
    if (tLim <= values.ambientC) {
      errors[values.tempLimit === 'custom' ? 'maxTempC' : 'tempLimit'] = `The temperature limit (${tLim} °C) must be above the ambient (${values.ambientC} °C).`;
    }
  }
  return { errors, payload: values };
}

// Display-only material properties (the backend computes the same for the maths).
export function materialDisplay(materialKey, iacs) {
  const m = MATERIAL_BY_KEY[materialKey];
  const i = Number(iacs);
  if (!m || !(i > 0)) return null;
  return {
    rho20NOhmM: 1000 / (58 * (i / 100)),
    alpha20: (m.alpha20 * i) / m.alphaRefIacs,
  };
}
